import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';

/**
 * CustomPopup — reusable popup select that replaces native <select> dropdowns.
 *
 * Options accept either plain strings or { value, label, description? } objects.
 *
 * Usage:
 *   <CustomPopup
 *     label="Variant"
 *     name="variant"
 *     value={form.variant}
 *     onChange={setField('variant')}          // receives { target: { name, value } }
 *     options={[{ value: 'pmt', label: 'PMT — Petrol Manual' }, ...]}
 *   />
 *
 * Extra props: placeholder, error, disabled, required, clearable,
 * searchable ('auto' shows a search box when there are more than 5 options).
 */
const normalize = (opt) =>
  typeof opt === 'object' && opt !== null
    ? { value: opt.value, label: opt.label ?? String(opt.value), description: opt.description }
    : { value: opt, label: String(opt), description: undefined };

const CustomPopup = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  error,
  disabled = false,
  required = false,
  clearable = false,
  searchable = 'auto',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => options.map(normalize), [options]);
  const selected = items.find((item) => item.value === value);

  const showSearch = searchable === true || (searchable === 'auto' && items.length > 5);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        String(item.value).toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
    );
  }, [items, query]);

  useEffect(() => {
    const onOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) close();
    };
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  useEffect(() => {
    if (open && showSearch && searchRef.current) searchRef.current.focus();
  }, [open, showSearch]);

  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const el = listRef.current.children[highlighted];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlighted]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setHighlighted(-1);
  };

  const toggle = () => {
    if (disabled) return;
    if (open) close();
    else {
      setOpen(true);
      setHighlighted(filtered.findIndex((item) => item.value === value));
    }
  };

  const select = (item) => {
    onChange({ target: { name, value: item.value } });
    close();
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        toggle();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && filtered[highlighted]) select(filtered[highlighted]);
    }
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          {label}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Trigger */}
        <div
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={`${name}-popup-listbox`}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          className={`w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm select-none transition-all duration-200 focus:outline-none focus:ring-2 ${
            error
              ? 'border-rose-400 bg-rose-50 focus:ring-rose-300 focus:border-rose-400'
              : open
              ? 'border-violet-400 ring-2 ring-violet-300/50 bg-white'
              : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400 hover:border-violet-300'
          } ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          <span className={`truncate ${selected ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <div className="flex items-center gap-0.5 shrink-0">
            {clearable && selected && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={clear}
                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <FiX className="h-3 w-3" />
              </span>
            )}
            <FiChevronDown
              className={`h-4 w-4 transition-all duration-200 ${open ? 'rotate-180 text-violet-500' : 'text-slate-400'}`}
            />
          </div>
        </div>

        {/* Popup */}
        {open && (
          <div className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 animate-fade-in-up">
            {showSearch && (
              <div className="p-2 border-b border-slate-100">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setHighlighted(0);
                    }}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Search…"
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-xl bg-slate-50 border border-slate-200 text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all"
                  />
                </div>
              </div>
            )}
            <ul
              id={`${name}-popup-listbox`}
              ref={listRef}
              role="listbox"
              className="max-h-56 overflow-y-auto py-1.5"
            >
              {filtered.length === 0 ? (
                <li className="px-4 py-3 text-sm text-slate-400 text-center">
                  No results for "<span className="font-medium text-slate-500">{query}</span>"
                </li>
              ) : (
                filtered.map((item, index) => {
                  const isSelected = item.value === value;
                  const isHighlighted = index === highlighted;
                  return (
                    <li key={String(item.value)} role="option" aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          select(item);
                        }}
                        onMouseEnter={() => setHighlighted(index)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                          isSelected
                            ? 'bg-violet-600 text-white font-semibold'
                            : isHighlighted
                            ? 'bg-violet-50 text-violet-700'
                            : 'text-slate-700 hover:bg-violet-50 hover:text-violet-700'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate">{item.label}</span>
                          {item.description && (
                            <span className={`block truncate text-[11px] ${isSelected ? 'text-violet-200' : 'text-slate-400'}`}>
                              {item.description}
                            </span>
                          )}
                        </span>
                        {isSelected && <FiCheck className="h-3.5 w-3.5 shrink-0" />}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>

      {error && <p className="text-rose-500 text-xs mt-1.5 font-medium">{error}</p>}
    </div>
  );
};

export default CustomPopup;
